import { db } from '../firebase';
import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  limit, 
  startAfter,
  startAt,
  endBefore,
  limitToLast,
  serverTimestamp,
  collectionGroup,
  getCountFromServer,
  onSnapshot
} from 'firebase/firestore';

/**
 * Filter out null or undefined values from an object
 * @param {Object} obj - Object to filter
 * @returns {Object} Filtered object
 */
const filterNullUndefined = (obj) => {
  return Object.fromEntries(
    Object.entries(obj).filter(([_, value]) => value !== null && value !== undefined)
  );
};

/**
 * Firebase service for hierarchical collections like sinif/ders/kazanim/yorum
 */
const HierarchicalServices = {
  /**
   * Get items from a hierarchical collection
   * @param {Object} options - Query options
   * @param {string} options.collection - Collection name (sinif, ders, kazanim, etc.)
   * @param {Object} options.parentIds - Parent document IDs (e.g. {sinif_id: "abc", ders_id: "def"})
   * @param {Object} options.filter - Additional filter conditions
   * @param {string} options.sort - Field to sort by
   * @param {string} options.order - Sort order ('asc' or 'desc')
   * @param {number} options.page - Page number for pagination
   * @param {number} options.perPage - Items per page
   */


  collections: {
    schools: ["schools"],
    students: ["schools", "students"],
    users: ["users"],
    libraries: ["schools", "libraries"],
    library_books: ["schools", "libraries", "library_books"],
    library_settings: ["schools", "libraries", "library_settings"],
    book_loans: ["schools", "libraries", "book_loans"],
    books: ["books"],
    tasks: ["tasks"],
    schema: ["schema"],
    contacts: ["contacts"],
    sinif: ["sinif"],
    ders: ["sinif", "ders"],
    files: ["sinif", "ders", "files"],
    kazanim: ["sinif", "ders", "kazanim"],
    slider: ["slider"],
    kaynak: ["sinif", "ders", "kazanim", "kaynak"],
    units: ["sinif", "ders", "units"],
    subjects: ["sinif", "ders", "units", "subjects"],
  },


  /**
   * Extract all string and number values from an object, clean them and return as array
   * Splits strings into individual words, converts strings to lowercase, and numbers to strings
   * @param {Object} obj - Object to process
   * @returns {Array} Array of cleaned string values
   */
  extractCleanValues: (obj) => {
    const values = [];
    
    const processValue = (value) => {
      if (typeof value === 'string') {
        // Convert string to lowercase and split by spaces
        const words = value.toLowerCase().split(/\s+/);
        words.forEach(word => {
          if (word) values.push(word);
        });
      } else if (typeof value === 'number') {
        // Convert number to string
        values.push(value.toString());
      } else if (typeof value === 'object' && value !== null) {
        // Process nested objects and arrays recursively
        if (Array.isArray(value)) {
          value.forEach(item => processValue(item));
        } else {
          Object.values(value).forEach(item => processValue(item));
        }
      }
    };
    
    processValue(obj);
    return values;
  },
  
  /**
   * Get collections for a given collection
   * @param {string} collection - Collection name
   * @param {string} type - Type of collections to get ('delete' or 'ids')
   * @param {Array} ids - Array of IDs to filter collections
   * @returns {Array} Array of collections
   */

  normalizeIds: (obj = {}) => {
    if (Array.isArray(obj.ids) && obj.ids.length > 0) {
      return obj.ids;
    }
    if (obj.id) {
      return [obj.id];
    }
    return [];
  },

  getCollections: function(collection, ids) {
    let selectCollections = HierarchicalServices.collections[collection];
    let allPaths = [];
    let index = 0;
    for(let col of selectCollections){
      allPaths.push(col);
      if(ids?.[index]){ 
        allPaths.push(ids[index]);
      }
      index++;
    }
    return allPaths.join('/');
  },

  buildCollectionQuery: (col, obj = {}) => {
    const {
      ids,
      filter = {},
      sort = 'created',
      order = 'desc',
      perPage = 20,
      getAll = false,
      search = false
    } = obj;

    const collectionPath = HierarchicalServices.getCollections(col, ids);
    if (!collectionPath) {
      throw new Error(`Invalid collection: ${col} or missing parent IDs`);
    }

    let q = collection(db, collectionPath);
    const conditions = [];

    Object.entries(filter).forEach(([key, value]) => {
      if (typeof value === 'object' && value !== null) {
        if (value.operator && value.value !== undefined) {
          conditions.push(where(key, value.operator, value.value));
        } else if (value.min !== undefined) {
          conditions.push(where(key, '>=', value.min));
        }
        if (value.max !== undefined) {
          conditions.push(where(key, '<=', value.max));
        }
      } else {
        conditions.push(where(key, '==', value));
      }
    });

    if (sort) {
      conditions.push(orderBy(sort, order));
    }

    if (search) {
      const searchArray = HierarchicalServices.extractCleanValues({ search });
      conditions.push(where('keywords', 'array-contains-any', searchArray));
    }

    if (conditions.length > 0) {
      q = query(q, ...conditions);
    }

    if (!getAll) {
      if (obj.lastDocument) {
        q = query(q, startAfter(obj.lastDocument), limit(perPage));
      } else {
        q = query(q, limit(perPage));
      }
    }

    return q;
  },

  getItems: async (col, obj = {}) => {
    try {
      const {
        count = false,
        perPage = 20,
        getAll = false,
      } = obj;

      const q = HierarchicalServices.buildCollectionQuery(col, obj);

      if(count){
        const countSnapshot = await getCountFromServer(q);
        const totalItems = countSnapshot.data().count;
        return {items: [], totalItems};
      }



      const querySnapshot = await getDocs(q);
      const items = [];
      querySnapshot.forEach((doc) => {
        items.push({
          id: doc.id,
          ...doc.data(),
        });
      });

      // Return pagination data if needed
      if (!getAll) {
        const lastVisible = querySnapshot.docs.length > 0 ? querySnapshot.docs[querySnapshot.docs.length - 1] : null;
        
        return {
          items,
          totalItems: obj.totalItems || items.length,
          totalPages: obj.totalPages || Math.ceil(items.length / perPage),
          lastDocument: lastVisible
        };
      }

      return { items };
    } catch (e) {
      console.error(`Error fetching items:`, e);
      return { error: e.message };
    }
  },

  subscribeToItems: (col, obj = {}, callback) => {
    try {
      const q = HierarchicalServices.buildCollectionQuery(col, obj);
      return onSnapshot(
        q,
        (querySnapshot) => {
          const items = [];
          querySnapshot.forEach((docSnap) => {
            items.push({ id: docSnap.id, ...docSnap.data() });
          });
          callback(items);
        },
        (e) => {
          console.error('Error in collection snapshot:', e);
          callback([], e);
        }
      );
    } catch (e) {
      console.error('Error subscribing to items:', e);
      callback([], e);
      return () => {};
    }
  },

  subscribeToItem: (col, obj = {}, callback) => {
    try {
      const ids = HierarchicalServices.normalizeIds(obj);
      if (!ids.length) {
        throw new Error('ID is required');
      }

      const docPath = HierarchicalServices.getCollections(col, ids);
      const docRef = doc(db, docPath);

      return onSnapshot(
        docRef,
        (docSnap) => {
          if (docSnap.exists()) {
            callback({ id: docSnap.id, ...docSnap.data() });
          } else {
            callback(null);
          }
        },
        (e) => {
          console.error('Error in document snapshot:', e);
          callback(null, e);
        }
      );
    } catch (e) {
      console.error('Error subscribing to item:', e);
      callback(null, e);
      return () => {};
    }
  },

  /**
   * Get a single item by ID
   */
  getItem: async (col, obj = {}) => {
    try {
      const ids = HierarchicalServices.normalizeIds(obj);
      if (!ids.length) {
        throw new Error('ID is required');
      }
      // Build the document path
      let docPath = HierarchicalServices.getCollections(col, ids);
      const docRef = doc(db, docPath);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data(),
        };
      } else {
        return { error: "Document not found" };
      }
    } catch (e) {
      console.error(`Error fetching item:`, e);
      return { error: e.message };
    }
  },

  /**
   * Add a new item to a collection
   */
  addItem: async function(col, obj = {}) {
    let {ids, keywords, ...data} = obj;
    if(keywords) data.keywords = HierarchicalServices.extractCleanValues(data);
    
    try {
      let collectionPath = HierarchicalServices.getCollections(col, ids);      
      if (!collectionPath) {
        throw new Error(`Invalid collection: ${col} or missing parent IDs`);
      }
      // Filter out null and undefined values
      const filteredData = filterNullUndefined(data);

      // Add timestamps and parent IDs to the data
      const itemWithMetadata = {
        ...filteredData,
        created: serverTimestamp(),
        updated: serverTimestamp()
      };

    
      const collectionRef = collection(db, collectionPath);
      const docRef = await addDoc(collectionRef, itemWithMetadata);
      
      return {
        id: docRef.id,
        ...itemWithMetadata,
      };
    } catch (e) {
      console.error(`Error adding item:`, e);
      return { error: e.message };
    }
  },


  /**
   * Update an existing item
   */
  updateItem: async (col, obj = {}) => {
    try {
      const ids = HierarchicalServices.normalizeIds(obj);
      if (!ids.length) {
        throw new Error('ID is required');
      }

      const {
        id,
        ids: _ids,
        keywords,
        ...data
      } = obj;

      if(keywords) data.keywords = HierarchicalServices.extractCleanValues(obj);

      // Build the document path
      let docPath = HierarchicalServices.getCollections(col, ids);
      if (!docPath) {
        throw new Error('Invalid collection: ' + col + ' or missing parent IDs');
      }

      // Filter out null and undefined values
      const filteredData = filterNullUndefined(data);

      // Add updated timestamp
      const updateData = {
        ...filteredData,
        updated: serverTimestamp()
      };
      
      const docRef = doc(db, docPath);
      await updateDoc(docRef, updateData);
      
      // Return updated document
      const updatedDoc = await getDoc(docRef);
      return {
        id: updatedDoc.id,
        ...updatedDoc.data(),
      };
    } catch (e) {
      console.error(`Error updating item:`, e);
      return { error: e.message };
    }
  },

  /**
   * Delete an item
   */
  deleteItem: async (col, obj = {}) => {
    try {
      const ids = HierarchicalServices.normalizeIds(obj);
      const { id, ids: _ids, ...data } = obj;

      if (!ids.length) {
        throw new Error('ID is required');
      }
      // Build the document path
      let docPath = HierarchicalServices.getCollections(col, ids);
      if (!docPath) { 
        throw new Error('Invalid collection: ' + col + ' or missing parent IDs');
      }
      const docRef = doc(db, docPath);
      await deleteDoc(docRef);
      return { success: true, data};
    } catch (e) {
      console.error(`Error deleting item:`, e);
      return { error: e.message };
    }
  },

  /**
   * Upload a file to Firebase Storage
   * @param {File} file - The file to upload
   * @param {Object} options - Upload options
   * @param {Array} options.ids - Parent IDs for path construction
   * @returns {Promise} Promise with download URL
   */
  uploadFile: async (file, options = {}) => {
    
    try {
      if (!file) {
        throw new Error('No file provided');
      }

      // Import Firebase storage only when needed
      const { getStorage, ref: storageRef, uploadBytesResumable, getDownloadURL } = await import('firebase/storage');
      const storage = getStorage();

      // Create path based on options
      const { ids = [], url = null } = options;
      
      let filePath;
      let fileReference;
      
      // If URL is provided, use the existing path instead of creating a new one
      if (url) {
        // Extract the path from the URL or use the provided path in options
        if (options.path) {
          filePath = options.path;
        } else {
          // Extract path from URL - this is a simplified approach, might need adjustment
          // based on how your Firebase Storage URLs are formatted
          const urlObj = new URL(url);
          const pathMatch = urlObj.pathname.match(/o\/(.+?)(?:\?|$)/);
          filePath = pathMatch ? decodeURIComponent(pathMatch[1]) : null;
          
          if (!filePath) {
            console.warn('Could not extract path from URL, generating new file');
          }
        }
      }
      
      // If no path was extracted from URL or URL wasn't provided, generate a new path
      if (!filePath) {
        const fileExtension = file.name.split('.').pop();
        
        // Generate a random string for the file name
        const generateRandomString = (length = 12) => {
          const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
          let result = '';
          for (let i = 0; i < length; i++) {
            result += chars.charAt(Math.floor(Math.random() * chars.length));
          }
          return result;
        };
        
        const randomName = generateRandomString();
        const fileName = `${randomName}.${fileExtension}`;
        
        // Build path including parent IDs if provided
        if (ids && ids.length > 0) {
          // Use parent IDs to create a hierarchical structure if needed
          filePath = `files/${ids.join('/')}/${fileName}`;
        } else {
          filePath = `files/${fileName}`;
        }
      }

      fileReference = storageRef(storage, filePath);
      const uploadTask = uploadBytesResumable(fileReference, file);

      return new Promise((resolve, reject) => {
        uploadTask.on('state_changed',
          (snapshot) => {
            // Optional: Track upload progress
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            console.log('Upload is ' + progress + '% done');
          },
          (error) => {
            // Handle unsuccessful uploads
            console.error('Error uploading file:', error);
            reject({ error: error.message });
          },
          async () => {
            // Handle successful uploads
            try {
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
              resolve({
                url: downloadURL,
                path: filePath,
                name: filePath.split('/').pop(),
                type: file.type,
                size: file.size
              });
            } catch (e) {
              reject({ error: e.message });
            }
          }
        );
      });
    } catch (e) {
      console.error(`Error uploading file:`, e);
      return { error: e.message };
    }
  },

  uploadImage: async (file, options = {}, path = 'dersapp') => {
    try {
      if (!file) {
        throw new Error('No file provided');
      }

      // Import Firebase storage only when needed
      const { getStorage, ref: storageRef, uploadBytesResumable, getDownloadURL } = await import('firebase/storage');
      const storage = getStorage();

      // Create path based on options
      const { ids = [], url = null } = options;
      
      let filePath;
      let fileReference;
      
      // If URL is provided, use the existing path instead of creating a new one
      if (url) {
        // Extract the path from the URL or use the provided path in options
        if (options.path) {
          filePath = options.path;
        } else {
          // Extract path from URL - this is a simplified approach, might need adjustment
          // based on how your Firebase Storage URLs are formatted
          const urlObj = new URL(url);
          const pathMatch = urlObj.pathname.match(/o\/(.+?)(?:\?|$)/);
          filePath = pathMatch ? decodeURIComponent(pathMatch[1]) : null;
          
          if (!filePath) {
            console.warn('Could not extract path from URL, generating new file');
          }
        }
      }
      
      // If no path was extracted from URL or URL wasn't provided, generate a new path
      if (!filePath) {
        const fileExtension = file.name.split('.').pop();
        
        // Generate a random string for the file name
        const generateRandomString = (length = 12) => {
          const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
          let result = '';
          for (let i = 0; i < length; i++) {
            result += chars.charAt(Math.floor(Math.random() * chars.length));
          }
          return result;
        };
        
        const randomName = generateRandomString();
        const fileName = `${randomName}.${fileExtension}`;
        
        // Build path including parent IDs if provided
        if (ids && ids.length > 0) {
          // Use parent IDs to create a hierarchical structure if needed
          filePath = `${path}/${ids.join('/')}/${fileName}`;
        } else {
          filePath = `${path}/${fileName}`;
        }
      }

      fileReference = storageRef(storage, filePath);
      const uploadTask = uploadBytesResumable(fileReference, file);

      return new Promise((resolve, reject) => {
        uploadTask.on('state_changed',
          (snapshot) => {
            // Optional: Track upload progress
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            console.log('Upload is ' + progress + '% done');
          },
          (error) => {
            // Handle unsuccessful uploads
            console.error('Error uploading file:', error);
            reject({ error: error.message });
          },
          async () => {
            // Handle successful uploads
            try {
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
              resolve({
                url: downloadURL,
                path: filePath,
                name: filePath.split('/').pop(),
                type: file.type,
                size: file.size
              });
            } catch (e) {
              reject({ error: e.message });
            }
          }
        );
      });
    } catch (e) {
      console.error(`Error uploading file:`, e);
      return { error: e.message };
    }
  }
};

export default HierarchicalServices; 